import React, { useState, useCallback, useEffect } from "react"
import { Swatch } from "react-color/lib/components/common"
import type { ColorResult } from "react-color"
import ColorPicker from "./color-picker"
import ColorPickerInputGroup from "./color-picker-input-group"
import cx from "classnames"
import getColorInHex from "./get-color-in-hex"
import type { SavedColors } from "./saved-colors-addon"
import type { DesignColors } from "./design-colors-addon"
import { usePopper } from "react-popper"
import Portal from "../portal"
import Label from "../label"

export type ColorInputProps = {
  /** Label displayed above the input */
  label?: string
  /** Color change handler */
  onChange?: (color: ColorResult) => void
  /** Whether to show the transparency option */
  transparency?: boolean
  /** Current color value (any CSS color string) */
  value: string
  /** Saved colors configuration */
  savedColors?: SavedColors
  /** Colors detected in the current design, shown in the "In this design" section */
  designColors?: DesignColors
}

/**
 * A form input that displays the current color as a swatch with hex value,
 * and opens a color picker dropdown on click.
 */
const ColorInput = ({
  label,
  onChange,
  transparency,
  value,
  savedColors,
  designColors
}: ColorInputProps) => {
  const [colorTrigger, setColorTrigger] = useState<HTMLButtonElement | null>(
    null
  )
  const [colorPicker, setColorPicker] = useState<HTMLDivElement | null>(null)

  const [active, setActive] = useState(false)

  // Anchor the dropdown to the trigger button (not the wrapper) so that when
  // `flip` moves it above the trigger near the bottom of the viewport, it sits
  // against the input rather than jumping above the label.
  const { styles, attributes } = usePopper(colorTrigger, colorPicker, {
    placement: "bottom-start",
    modifiers: [
      {
        name: "flip",
        enabled: true
      },
      {
        // Keep a consistent gap from the trigger on both the default (below)
        // and flipped (above) placements.
        name: "offset",
        options: { offset: [0, 8] }
      }
    ]
  })

  const buttonClasses = cx(
    "flex items-center w-full bg-white border border-gray-200 rounded text-gray-800 focus:outline-none",
    {
      uppercase: getColorInHex(value) !== "transparent"
    }
  )

  const handleClick = () => {
    if (active) {
      return
    }
    setActive(true)
  }

  const handleChange = (color: ColorResult) => {
    if (onChange) onChange(color)
  }

  const handleClose = () => {
    setActive(false)
  }

  // The design overlay scale puts dialogs at z-[5000]/[5001] and tooltips at
  // z-[5002]. Use 5001 so the picker sits in that overlay layer but stays just
  // below the tooltip layer — its own tooltips portal at 5002 and must render
  // above the picker, not behind it.

  const popperStyle = {
    zIndex: 5001,
    ...styles.popper
  }

  const handleDocumentClick = useCallback(
    (event: MouseEvent) => {
      if (!colorTrigger) return
      if (!colorPicker) return

      const target = event.target as Node

      // Close unless the click landed on the trigger button or inside the
      // color picker. The label and other parts of the wrapper are not part
      // of the picker UI, so clicking them should close it.
      if (!colorTrigger.contains(target) && !colorPicker.contains(target)) {
        handleClose()
      }
    },
    [colorPicker, colorTrigger]
  )

  useEffect(() => {
    document.addEventListener("mousedown", handleDocumentClick)

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick)
    }
  }, [handleDocumentClick])

  const hex = getColorInHex(value)

  const swatchClass = hex === "#FFFFFF" ? " border border-gray-200" : ""

  return (
    <div className="relative">
      {label && (
        <Label as="div" className="mb-1">
          {label}
        </Label>
      )}

      <button
        ref={setColorTrigger}
        className={buttonClasses}
        onClick={handleClick}
      >
        <div
          className={
            "rounded overflow-hidden ml-2 flex-none h-6 w-6" + swatchClass
          }
        >
          <Swatch color={hex} />
        </div>
        <ColorPickerInputGroup color={hex} onChange={handleChange} />
      </button>
      <Portal>
        {active && (
          <div ref={setColorPicker} style={popperStyle} {...attributes.popper}>
            <ColorPicker
              onChange={handleChange}
              onClose={handleClose}
              active={active}
              color={hex}
              hexInput={false}
              position="relative"
              panelInFlow
              transparency={transparency}
              standalone={false}
              savedColors={savedColors}
              designColors={designColors}
            />
          </div>
        )}
      </Portal>
    </div>
  )
}

export { ColorInput }
export default ColorInput
